// Q4CL-client (Vite 版) エントリポイント
//
// 「機械的移植」 方針: 旧 client-ep (CDN + sfcLoader) の utils / stores / Vue ファイルが
// `window.xxx` / `Vue.ref()` / `_` / `moment` / `firebase` 等のグローバルに依存している。
// 一切書き換えず動かすため、 _libShim.js で npm パッケージを window へ shim する。
//
// import 順序 (重要):
//   1. _libShim を最初に — その中の window 代入が、 hoist 連鎖の先頭で実行される
//   2. utils / stores / firebase/config を side-effect import (IIFE が window.xxx を populate)
//   3. router / App をロード
//   4. createApp + globalProperties 設定 + mount

import './_libShim.js'

// アプリ独自スタイル (旧 public/assets/styles.css)。
// 内部で @import "tailwindcss" + @theme で v4 のテーマトークンを定義しているので、
// Tailwind ユーティリティ (bg-q4cl-primary 等) も同時に有効化される。
import './style.css'

// ===== utils / stores / settings を side-effect import =====
// それぞれの中で IIFE が走り、 window.xxx を populate する。
import './firebase/config.js'
import './settings/Q4CL.js'
import './utils/debugOpts.js'
import './utils/encryption.js'
import './utils/i18n.js'
import './utils/mixins.js'
import './utils/fieldSerializer.js'
import './stores/deviceStore.js'
import './stores/i18nStore.js'
import './stores/questionnaireStore.js'
// debug 用問診票 (window.DEBUG_QST_DEF) を常時セット。 ?debug=true でのみ使われる。
// 旧 client-ep では Home.vue が動的 <script> タグで遅延ロードしていたが、 Vite では
// src/views/debugQstDef.js を直接 URL 取得できないため side-effect import に変更。
import './views/debugQstDef.js'

// ===== router & App =====
import router from './router/index.js'
import App from './App.vue'

const app = window.Vue.createApp(App)

// 旧 app.js 踏襲: URL クエリ screentype=smartphone|tablet|desktop を body の data-screentype に反映
try {
  const sp = new URLSearchParams(window.location.search)
  const st = sp.get('screentype')
  if (st && ['smartphone', 'tablet', 'desktop'].includes(st)) {
    document.body.dataset.screentype = st
  }
} catch (e) { /* ignore */ }

// 旧 client 互換のグローバル参照 ($firebase / $Q4CL / $encryption / $i18nStore / $mixins /
// $device / $questionnaire / $fieldSerializer) — 一部 component が使う可能性ありで全部維持
app.config.globalProperties.$firebase = window.firebase
app.config.globalProperties.$Q4CL = window.Q4CL
app.config.globalProperties.$encryption = window.encryption
app.config.globalProperties.$i18nStore = window.i18nStore
app.config.globalProperties.$mixins = window.mixinUtils
app.config.globalProperties.$device = window.deviceStore
app.config.globalProperties.$questionnaire = window.questionnaireStore
app.config.globalProperties.$fieldSerializer = window.fieldSerializer

// i18n のグローバルヘルパ ($t / $tAs) — template で {{ $t('送信履歴') }} 等で使う
app.config.globalProperties.$t = (s, opt) => {
  if (!window.i18nStore) return s
  return window.i18nStore.i18n(s, opt)
}
app.config.globalProperties.$tAs = async (s) => {
  if (!window.i18nStore) return s
  return await window.i18nStore.i18nAs(s)
}

// エラーハンドラ
app.config.errorHandler = (err, _instance, info) => {
  console.error('[Vue Error]', err, info)
  if (err?.stack) console.error('[Vue Error] Stack:', err.stack)
}
app.config.warnHandler = (msg, _instance, trace) => {
  console.warn('[Vue Warn]', msg, trace)
}

app.use(router)
app.mount('#app')
// 開発環境と ?debug=true のときだけ (_test_playwright.py がこのログでマウント完了を確認する)
if (/^(localhost|127\.0\.0\.1)$/.test(location.hostname) || new URLSearchParams(location.search).get('debug') === 'true') {
  console.log('[Q4CL] app mounted')
}
